import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { withNamespaces } from "react-i18next";
import { Tooltip, Label, Spinner } from "reactstrap";

import UserUtils from "src/services/utils/UserUtils";
import ReportIncidenService from "../../../../components/api/ReportIncidenService";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { AvField, AvForm } from "availity-reactstrap-validation";
import debounce from "lodash.debounce";

const Index = (props) => {
  const { t, report, caseId, currentUser, reportLoading, token } = props;
  const [adminDescription, setAdminDescription] = useState("");
  const [descriptionTooltip, setDescriptionTooltip] = useState(false);
  const userUtils = new UserUtils();

  const isAnalystOrAnalystAdmin = () => {
    return userUtils.isAnalystOrAnalystAdmin(currentUser);
  };

  const isAnalystAdmin = () => {
    return userUtils.isAnalystAdmin(currentUser);
  };

  const handleUpdateAdminDescriptionMutation = useMutation({
    mutationFn: async (payload) => {
      const service = ReportIncidenService.getInstance();

      return await service.UpdateAdminDescription(payload);
    },
  });

  const handleChangeUpdateAdminDescription = useCallback(
    debounce((value) => {
      handleUpdateAdminDescriptionMutation.mutate(
        {
          caseId: caseId,
          description: value,
        },
        {
          onError: (error) => {
            toast(t("Failed to update admin description."), {
              type: "error",
            });
            // Refetch description if mutation fails
            setAdminDescription(report?.admin_description);
          },
        }
      );
    }, 1500),
    []
  );


  // Set admin description when report loading changes
  useEffect(() => {
    if (!reportLoading) {
      setAdminDescription(report?.admin_description);
    }
  }, [reportLoading, report?.admin_description]);

  const handleDescriptionChange = (e) => {
    const value = e.target.value;
    setAdminDescription(value);
    if (report?.admin_description?.trim() !== value.trim()) {
      handleChangeUpdateAdminDescription(value);
    }
  };

  const renderSavingStatus = () => {
    if(handleUpdateAdminDescriptionMutation.isLoading){
      return (
        <>
          <Spinner className="me-1" size='sm' />
          <span className="text-muted">
            {t('Saving')}...
          </span>
        </>
      )
    }

    if(handleUpdateAdminDescriptionMutation.isSuccess){
      return (
        <span className="text-success">
          <i className="ri-check-line align-middle font-size-18" />
          {t('Saved')}
        </span>
      )
    }

    if(handleUpdateAdminDescriptionMutation.isError){
      return (
        <span className="text-danger">
          {t('Save failed')}
        </span>
      )
    }
  }

  useEffect(() => {
    if(handleUpdateAdminDescriptionMutation.isSuccess){
      setTimeout(() => {
        handleUpdateAdminDescriptionMutation.reset();
      }, 3000);
    }
  }, [ handleUpdateAdminDescriptionMutation.status ]);

  return (
    <div  >
      {/* Description by the admin*/}
      <Label style={{ display: "flex", justifyContent: "space-between" }}>
        <span>
          {t("Description by the admin")}:
          {!report?.is_deleted_content &&
          (isAnalystAdmin ||
            report?.assignedAnalyst?.value === currentUser?.id) ? (
            <span className="ms-2 me-2">
              <i
                className="fa fa-info-circle"
                aria-hidden="true"
                id="descriptionTooltip"
              />
              <Tooltip
                placement="top"
                isOpen={descriptionTooltip}
                target="descriptionTooltip"
                toggle={() => setDescriptionTooltip(!descriptionTooltip)}
              >
                {t(
                  "Please do not use clear names in the description. The description will remain in the deletion protocol"
                )}
                .
              </Tooltip>
            </span>
          ) : null}
          {renderSavingStatus()}
        </span>
      </Label>
      {reportLoading ? (
        <p
          className={`dt-field dt-skeleton ${
            isAnalystOrAnalystAdmin ? "dt-select-list" : ""
          }`}
        ></p>
      ) : (
        <AvForm>
          {!report?.is_deleted_content &&
          (isAnalystAdmin ||
            (report?.assignedAnalyst &&
              report?.assignedAnalyst.value === currentUser?.id)) ? (
            <AvField
              type="textarea"
              name="description"
              disabled={report?.deleted_by_name || report?.is_deleted_content || report?.is_read_only_analyst}
              className={report?.deleted_by_name || report?.is_deleted_content || report?.is_read_only_analyst ? "disabled" : ""}
              placeholder={t("Write a summary")}
              value={report?.admin_description}
              onChange={handleDescriptionChange}
              style={{
                maxHeight: "239px",
                transition: "all ease 0.3s",
                height: "114px",
                width:'100%',
              }}
            />
          ) : (
            <p className="dt-field">{report?.admin_description}</p>
          )}
        </AvForm>
      )}
    </div>
  );
};

const mapStatetoProps = (state) => {
  const { token, currentUser } = state.Login;
  return {
    currentUser,
    token,
  };
};

export default withNamespaces()(connect(mapStatetoProps)(Index));
